<template>
  <div class="flex flex-col gap-3 rounded-xl border border-gray-100 p-4">
    <div class="flex items-center gap-2 min-w-0">
      <v-icon size="18" color="grey-darken-1">{{ mdiOfficeBuildingOutline }}</v-icon>
      <p
        data-testid="doctor-invite-company"
        class="text-base font-medium text-gray-900 capitalize truncate"
      >
        {{ link.company.name.toLowerCase() }}
      </p>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-gray-500">Status da empresa</p>
        <LabelBadge
          v-if="companyStatus"
          :label="companyStatus.label"
          :color="companyStatus.color"
          :icon="null"
          size="small"
          class="mt-1"
        />
        <p v-else class="text-base text-gray-900">—</p>
      </div>

      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-gray-500">Status do médico</p>
        <LabelBadge
          v-if="doctorStatus"
          :label="doctorStatus.label"
          :color="doctorStatus.color"
          :icon="null"
          size="small"
          class="mt-1"
        />
        <p v-else class="text-base text-gray-900">—</p>
      </div>
    </div>

    <div v-if="inviteUrl" class="pt-4 border-t border-gray-100">
      <OutlineButton
        data-testid="doctor-copy-invite-link-button"
        :prepend-icon="isCopied ? mdiCheck : mdiContentCopy"
        width="100%"
        height="44px"
        class="font-bold"
        @click="copy"
      >
        {{ isCopied ? 'Link copiado!' : 'Copiar link do convite' }}
      </OutlineButton>
    </div>

    <div v-if="canResend" class="pt-4 border-t border-gray-100">
      <OutlineButton
        data-testid="doctor-resend-invite-button"
        :prepend-icon="mdiEmailSyncOutline"
        :loading="isResending"
        width="100%"
        height="44px"
        class="font-bold"
        @click="showResendConfirm = true"
      >
        Reenviar convite
      </OutlineButton>
    </div>
  </div>

  <ConfirmActionModal
    :show="showResendConfirm"
    title="Reenviar convite"
    :description="`Deseja reenviar o convite para ${link.company.name}? Um novo convite pendente será criado.`"
    text-button="Reenviar"
    :is-loading="isResending"
    @close-modal="showResendConfirm = false"
    @handle-confirm="handleResend"
  />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { mdiOfficeBuildingOutline, mdiContentCopy, mdiCheck, mdiEmailSyncOutline } from '@mdi/js'
import { useToast } from 'vue-toastification'

import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import ConfirmActionModal from '@/components/designSystem/modals/confirm.delete.action.vue'
import type { DoctorDetailCompanyLink } from '@/Network/Types/Responses/CompanyResponses'
import { CompanyDoctorInviteStatusEnum } from '@/Network/Types/Requests/CompanyRequests'
import { useDoctorDetailStore } from '@/pinia-store/useDoctorDetailStore'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import { getInviteStatusConfig } from '../../helpers/doctorStatus'
import { getInviteErrorMessage } from '../../helpers/inviteErrors'
import { useInviteLink } from '../../helpers/useInviteLink'

const props = defineProps<{
  link: DoctorDetailCompanyLink
}>()

const toast = useToast()
const detailStore = useDoctorDetailStore()
const permissionsStore = usePermissionsStore()

const companyStatus = computed(() => getInviteStatusConfig(props.link.invite?.companyStatus))
const doctorStatus = computed(() => getInviteStatusConfig(props.link.invite?.doctorStatus))

const { inviteUrl, isCopied, copy } = useInviteLink(() => props.link.invite?.hash)

// Somente DECLINED por ora; EXPIRED/CANCELLED dependem de suporte no backend.
const RESENDABLE_DOCTOR_STATUSES = new Set<string>([CompanyDoctorInviteStatusEnum.DECLINED])

const canResend = computed(() => {
  const status = props.link.invite?.doctorStatus
  if (!status || !RESENDABLE_DOCTOR_STATUSES.has(status)) return false
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.CompanyDoctorInviteCreating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  })
})

const isResending = ref(false)
const showResendConfirm = ref(false)

async function handleResend() {
  try {
    isResending.value = true
    await detailStore.resendInvite(props.link.company.id)
    showResendConfirm.value = false
    toast.success('Convite reenviado com sucesso.')
  } catch (error) {
    console.error('Erro ao reenviar convite:', error)
    // Guard de domínio (convite já ativo → mensagem curada): exibido direto,
    // pois como fallback de notifyError a mensagem curada nunca apareceria.
    toast.error(
      getInviteErrorMessage(error, 'Não foi possível reenviar o convite. Tente novamente.')
    )
  } finally {
    isResending.value = false
  }
}
</script>
